import { useCallback, useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from '../util/router';
import { useCityStore } from '../stores';
import { getHomeBanners } from '../util/util';

const AUTO_ADVANCE_MS = 5000;
const DISMISS_KEY = 'showrunner.home_banner_dismissed_until';
const DISMISS_MS = 24 * 60 * 60 * 1000;

const INTERNAL_URL = /^(?:https?:\/\/(?:www\.)?showrunner\.co|myapp:\/)(\/(?:lists|show|profile|seen_shows|seen-shows|org)\/[^#\s]*)/i;

function internalPath(url) {
  const match = url.trim().match(INTERNAL_URL);
  return match ? match[1].replace(/^\/seen_shows\//, '/seen-shows/') : null;
}

function isDismissed() {
  try {
    const until = Number(localStorage.getItem(DISMISS_KEY));
    return Number.isFinite(until) && Date.now() < until;
  } catch {
    return false;
  }
}

function dismissForOneDay() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
  } catch {}
}

function HomeBannerModal({ banner, onClose, onOpenLink }) {
  return (
    <Dialog open onClose={onClose} className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <Dialog.Panel className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-white">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-full p-1 hover:bg-black/5"
        >
          <XMarkIcon className="h-5 w-5 text-slate-500" />
        </button>
        <div className="space-y-3 px-5 pb-5 pt-5">
          <div className="pr-8">
            <Dialog.Title className="text-xl font-semibold leading-7 text-black">
              {banner.title}
            </Dialog.Title>
            {banner.subtitle && (
              <p className="mt-1 text-sm font-bold text-black/60">{banner.subtitle}</p>
            )}
          </div>
          {banner.description && (
            <p className="whitespace-pre-line font-oswald text-sm leading-6 text-black/80">
              {banner.description}
            </p>
          )}
          {banner.link_out && (
            <button
              type="button"
              onClick={onOpenLink}
              className="pt-2 text-sm font-semibold text-dark-red hover:underline"
            >
              Learn more &rarr;
            </button>
          )}
        </div>
      </Dialog.Panel>
    </Dialog>
  );
}

function HomeBanner({ hidden = false }) {
  const navigate = useNavigate();
  const { selectedCity } = useCityStore();
  const [banners, setBanners] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [modalBanner, setModalBanner] = useState(null);
  const [dismissed, setDismissed] = useState(isDismissed);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (dismissed) return undefined;
    let cancelled = false;
    getHomeBanners(selectedCity)
      .then(data => {
        if (cancelled) return;
        setBanners(data);
        setActiveIndex(0);
      })
      .catch(error => {
        console.error('Failed to load home banners:', error);
        if (!cancelled) setBanners([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCity, dismissed]);

  useEffect(() => {
    if (banners.length <= 1 || modalBanner || paused) return undefined;
    const timer = setInterval(() => setActiveIndex(i => (i + 1) % banners.length), AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [banners.length, modalBanner, paused, activeIndex]);

  const openLink = useCallback(
    url => {
      const path = internalPath(url);
      if (path) navigate(path);
      else window.open(url, '_blank', 'noopener,noreferrer');
    },
    [navigate]
  );

  const handleDismiss = () => {
    setModalBanner(null);
    setDismissed(true);
    setBanners([]);
    dismissForOneDay();
  };

  if (hidden || dismissed || banners.length === 0) return null;

  const banner = banners[Math.min(activeIndex, banners.length - 1)];
  const clickable = banner.is_modal || !!banner.link_out;

  return (
    <>
      <div
        className="bg-black text-white"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        <div className="mx-auto flex max-w-[90rem] items-center gap-3 px-4 py-2 md:px-8">
          <button
            type="button"
            disabled={!clickable}
            onClick={() => (banner.is_modal ? setModalBanner(banner) : openLink(banner.link_out))}
            className="min-w-0 flex-1 text-left disabled:cursor-default"
          >
            <p className="truncate text-xs font-semibold md:text-sm">{banner.title}</p>
            {banner.subtitle && (
              <p className="truncate text-[11px] text-white/80 md:text-xs">{banner.subtitle}</p>
            )}
          </button>

          {banners.length > 1 && (
            <div className="flex items-center gap-1.5" role="tablist" aria-label="Announcements">
              {banners.map((b, index) => (
                <button
                  key={b.id ?? `${b.title}-${index}`}
                  type="button"
                  role="tab"
                  aria-selected={index === activeIndex}
                  aria-label={`Announcement ${index + 1} of ${banners.length}`}
                  onClick={() => setActiveIndex(index)}
                  className="p-1"
                >
                  <span
                    className={`block rounded-full ${
                      index === activeIndex ? 'h-1.5 w-1.5 bg-white' : 'h-1 w-1 bg-white/40'
                    }`}
                  />
                </button>
              ))}
            </div>
          )}

          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Hide announcements for a day"
            className="rounded p-1 hover:bg-white/10"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {modalBanner && (
        <HomeBannerModal
          banner={modalBanner}
          onClose={() => setModalBanner(null)}
          onOpenLink={() => {
            setModalBanner(null);
            openLink(modalBanner.link_out);
          }}
        />
      )}
    </>
  );
}

export default HomeBanner;
